import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { Job, SyncInfo, TimeMapping } from "../api/types";
import { Badge, KeyValue, Spinner, StateDot } from "./ui";
import { useApp } from "../state/AppContext";

/**
 * Sync inspector.
 *
 * Two different claims are shown separately because they fail separately:
 * how tightly frames pair (a distribution over the whole recording), and
 * whether the absolute inter-eye offset can be trusted at all (a yes/no that
 * depends on the two files sharing one capture epoch — see ADR 0003).
 */
export function SyncInspector({
  sessionId,
  sync,
  mappings,
  series,
  frameIntervalNs,
  onChanged,
}: {
  sessionId: string;
  sync: SyncInfo | null;
  mappings: Record<string, TimeMapping>;
  series: { left_ns: number[]; delta_ms: number[]; valid: boolean[] } | null;
  frameIntervalNs: number | null;
  onChanged: () => void;
}) {
  const { notify, jobs } = useApp();
  const [busy, setBusy] = useState(false);
  const [estimateJob, setEstimateJob] = useState<Job | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const rightOffsetNs = mappings["camera.right"]?.segments?.[0]?.b ?? 0;
  const rightDrift = mappings["camera.right"]?.segments?.[0]?.a ?? 1;

  useEffect(() => {
    if (!estimateJob) return;
    const latest = jobs.find((j) => j.id === estimateJob.id);
    if (latest && latest.state !== estimateJob.state) {
      setEstimateJob(latest);
      if (latest.state === "succeeded") {
        const r: any = latest.result;
        notify(
          r?.available
            ? `Audio estimate: ${(r.offset_ns / 1e6).toFixed(2)} ms at correlation ${Number(r.confidence).toFixed(2)}${r.applied ? " — applied" : ""}`
            : `No estimate: ${r?.reason ?? "unavailable"}`,
          r?.available ? "success" : "info",
        );
        onChanged();
      } else if (latest.state === "failed") {
        notify(latest.error ?? "estimate failed", "error");
      }
    }
  }, [jobs, estimateJob, notify, onChanged]);

  const nudge = useCallback(
    async (deltaNs: number) => {
      setBusy(true);
      try {
        await api.setOffset(sessionId, {
          stream_id: "camera.right",
          offset_ns: rightOffsetNs + deltaNs,
          drift: rightDrift,
          source: "manual",
          method: "manual_nudge",
        });
        onChanged();
      } catch (e: any) {
        notify(e.message, "error");
      } finally {
        setBusy(false);
      }
    },
    [sessionId, rightOffsetNs, rightDrift, onChanged, notify],
  );

  /* delta-over-time plot */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !series || series.left_ns.length === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = 108;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const css = getComputedStyle(document.documentElement);
    const c = (n: string) => css.getPropertyValue(n).trim();

    const t0 = series.left_ns[0];
    const t1 = series.left_ns[series.left_ns.length - 1];
    const limit = Math.max(sync?.max_pair_delta_ms ?? 16.7, ...series.delta_ms.map(Math.abs), 1) * 1.05;
    const x = (t: number) => ((t - t0) / Math.max(1, t1 - t0)) * (w - 34) + 30;
    const y = (ms: number) => h / 2 - (ms / limit) * (h / 2 - 10);

    ctx.strokeStyle = c("--line");
    ctx.beginPath();
    ctx.moveTo(30, y(0) + 0.5);
    ctx.lineTo(w - 4, y(0) + 0.5);
    ctx.stroke();

    const tol = sync?.max_pair_delta_ms ?? 16.7;
    ctx.fillStyle = "rgba(53,196,140,0.08)";
    ctx.fillRect(30, y(tol), w - 34, y(-tol) - y(tol));

    ctx.font = "9px ui-monospace, monospace";
    ctx.fillStyle = c("--text-3");
    ctx.fillText(`+${limit.toFixed(0)}`, 2, 11);
    ctx.fillText("0", 2, y(0) + 3);
    ctx.fillText(`-${limit.toFixed(0)}`, 2, h - 3);

    for (let i = 0; i < series.left_ns.length; i++) {
      ctx.fillStyle = series.valid[i] ? c("--good") : c("--bad");
      ctx.fillRect(x(series.left_ns[i]), y(series.delta_ms[i]) - 0.75, 1.5, 1.5);
    }
  }, [series, sync]);

  if (!sync) return <div className="hint" style={{ padding: 10 }}>Sync has not been computed for this session yet.</div>;

  // The frame step is the *measured* interval. Without it the button would be
  // nudging by an assumed 30 Hz, which is not this footage's frame interval.
  const stepNs = frameIntervalNs != null ? Math.round(frameIntervalNs) : null;
  const visual = sync.visual_sync;

  return (
    <div style={{ padding: 10, display: "flex", flexDirection: "column", gap: 12, overflow: "auto" }}>
      <div className="row">
        <Badge kind={sync.quality}><StateDot state={sync.quality} /> {sync.quality}</Badge>
        <Badge
          kind={sync.offset_verified ? "good" : "warning"}
          title={
            sync.offset_verified
              ? "Both files share one capture epoch, so the inter-eye offset is read from the recording"
              : sync.offset_asserted
                ? "The offset was set by hand or estimated. Frame pairing stays tight under any constant offset, so these statistics cannot confirm it."
                : "The two files do not share a capture epoch — the absolute offset is unverified"
          }
        >
          {sync.offset_verified ? "offset verified" : sync.offset_asserted ? "offset asserted" : "offset unverified"}
        </Badge>
        <span className="spacer" />
        <span className="tiny muted mono">{sync.source}</span>
      </div>

      {visual ? <div className="panel" style={{ padding: 10 }}>
        <div className="row" style={{ marginBottom: 5 }}>
          <strong>Automatic visual sync</strong>
          <Badge kind={visual.status === "aligned" ? "good" : "warning"}>
            {visual.status === "aligned" ? "Image estimated" : "Not applied"}
          </Badge>
        </div>
        <div className="tiny muted" style={{ lineHeight: 1.45 }}>
          {visual.status === "aligned"
            ? `${(visual.right_offset_ns / 1e6).toFixed(1)} ms right-eye correction${visual.uncertainty_ns != null ? ` · ±${(visual.uncertainty_ns / 1e6).toFixed(1)} ms estimated uncertainty` : ""}. ${visual.applied_to_media ? "Already saved in video timing; no second correction is applied." : "Applied through the review timeline."}`
            : visual.reason ?? "Motion did not provide a reliable timing estimate; capture timestamps were retained."}
        </div>
      </div> : null}

      {sync.notes.length > 0 ? (
        <div className="col" style={{ gap: 5 }}>
          {sync.notes.map((n, i) => (
            <div key={i} className="tiny" style={{ color: "var(--warn)", lineHeight: 1.45 }}>{n}</div>
          ))}
        </div>
      ) : null}

      <KeyValue
        rows={[
          ["Start offset", `${sync.start_offset_ms.toFixed(2)} ms  (right − left)`],
          ["Frame interval", `${sync.frame_interval_ms.toFixed(2)} ms  (${(1000 / sync.frame_interval_ms).toFixed(2)} fps)`],
          ["Median |Δ|", sync.pair_stats.median_ms != null
            ? `${sync.pair_stats.median_ms.toFixed(2)} ms  (${sync.median_delta_frames?.toFixed(2)} frames)`
            : "—"],
          ["p95 |Δ|", sync.pair_stats.p95_ms != null ? `${sync.pair_stats.p95_ms.toFixed(2)} ms` : "—"],
          ["Pairs", `${sync.pair_stats.valid} / ${sync.pair_stats.count} within ${sync.max_pair_delta_ms.toFixed(1)} ms`],
          ["Wide pairs", `${sync.wide_pairs.total} (${sync.wide_pairs.explained_by_frame_gap} from dropped frames, ${sync.wide_pairs.unexplained} unexplained)`],
          ["Unpaired", `${sync.unpaired_head} head · ${sync.unpaired_tail} tail`],
          ["Drift", sync.drift_ns_per_s != null ? `${(sync.drift_ns_per_s / 1e6).toFixed(3)} ms/s` : "—"],
          ["Audio check", sync.audio_check?.available
            ? `${(sync.audio_check.offset_ns! / 1e6).toFixed(2)} ms at correlation ${sync.audio_check.confidence!.toFixed(2)}`
            : sync.audio_check?.reason ?? "—"],
        ]}
      />

      <div>
        <div className="tiny muted" style={{ marginBottom: 4 }}>Pair delta over time (green = usable, red = too wide)</div>
        <canvas ref={canvasRef} style={{ width: "100%", height: 108, display: "block", background: "var(--bg-0)", borderRadius: 3, border: "1px solid var(--line)" }} />
      </div>

      <div>
        <div className="tiny muted" style={{ marginBottom: 5 }}>
          Manual correction — applied to the right eye's mapping, never to the recording
        </div>
        <div className="row" style={{ gap: 4, flexWrap: "wrap" }}>
          <button disabled={busy || stepNs == null} onClick={() => nudge(-stepNs!)}
                  title={stepNs == null ? "The frame interval has not been measured yet" : ""}>
            −1 frame
          </button>
          <button disabled={busy} onClick={() => nudge(-1_000_000)}>−1 ms</button>
          <button disabled={busy} onClick={() => nudge(1_000_000)}>+1 ms</button>
          <button disabled={busy || stepNs == null} onClick={() => nudge(stepNs!)}
                  title={stepNs == null ? "The frame interval has not been measured yet" : ""}>
            +1 frame
          </button>
          <span className="spacer" />
          <span className="tiny mono muted">offset {(rightOffsetNs / 1e6).toFixed(2)} ms</span>
        </div>
        <div className="row" style={{ gap: 6, marginTop: 8 }}>
          <button
            disabled={busy}
            onClick={async () => {
              const r = await api.estimateSync(sessionId, false);
              setEstimateJob(r.job);
              notify("Estimating offset from audio…");
            }}
          >
            {estimateJob && (estimateJob.state === "queued" || estimateJob.state === "running") ? <Spinner /> : null}
            Auto estimate
          </button>
          <button
            disabled={busy || visual != null}
            onClick={async () => {
              const r = await api.estimateSync(sessionId, true);
              setEstimateJob(r.job);
              notify("Estimating and applying if confident…");
            }}
          >
            Estimate &amp; apply
          </button>
          <button
            className="ghost"
            disabled={busy}
            onClick={async () => { await api.resetSync(sessionId); onChanged(); notify("Reset to the recorder's timestamps"); }}
          >
            Reset
          </button>
        </div>
        <div className="tiny muted" style={{ marginTop: 6, lineHeight: 1.45 }}>
          {visual
            ? "Audio estimates are diagnostic only for this recording. Both eyes use one phone microphone, so audio must not overwrite visual camera alignment. Manual review corrections remain available."
            : "The estimate compares the two files' microphone audio. It checks container audio timing, not simultaneous camera exposure. Corrections are applied only above a 0.5 correlation."}
        </div>
      </div>
    </div>
  );
}
